const toFiniteNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const normalizeCurrencyDecimals = (value) => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed)) return 2;
  return Math.min(Math.max(parsed, 0), 7);
};

export const roundMoney = (value, decimals = 2) => {
  const safeDecimals = normalizeCurrencyDecimals(decimals);
  const factor = 10 ** safeDecimals;
  const number = toFiniteNumber(value);
  const sign = number < 0 ? -1 : 1;
  return (
    sign * Math.round((Math.abs(number) + Number.EPSILON) * factor) / factor
  );
};

export const getCurrencyLabel = (settings = {}, language = "english") =>
  language === "arabic"
    ? settings.currency_local || settings.currency_english || settings.currency_iso || ""
    : settings.currency_english || settings.currency_iso || settings.currency_local || "";

export const getCurrencyPlacement = (settings = {}) => {
  const configured =
    settings.currency_placement ||
    settings.currency_position ||
    settings.price_currency_position;
  if (["before", "prefix", "start", "left"].includes(configured)) {
    return "before";
  }
  return "after";
};

export const formatMoney = (
  value,
  { settings = {}, language = "english", currency, placement } = {},
) => {
  const decimals = normalizeCurrencyDecimals(settings.currency_decimals);
  const amount = roundMoney(value, decimals).toFixed(decimals);
  const label = currency === undefined ? getCurrencyLabel(settings, language) : currency;
  if (!label) return amount;
  const resolvedPlacement = placement || getCurrencyPlacement(settings);
  return resolvedPlacement === "before"
    ? `${label} ${amount}`
    : `${amount} ${label}`;
};

export const getPreselectedOptionTotals = (options = []) =>
  (Array.isArray(options) ? options : []).reduce(
    (totals, option) => {
      const choices = Array.isArray(option && option.choices) ? option.choices : [];
      choices.forEach((choice) => {
        const quantity = Math.max(toFiniteNumber(choice.preselected), 0);
        if (!quantity) return;
        const price = Math.max(toFiniteNumber(choice.price), 0);
        const strikedPrice = Math.max(
          toFiniteNumber(choice.striked_price, price),
          price,
        );
        totals.current += price * quantity;
        totals.original += strikedPrice * quantity;
      });
      return totals;
    },
    { current: 0, original: 0 },
  );

const getMoneyObjectAmount = (money) => {
  if (!money || !Number.isFinite(Number(money.amount_minor))) return null;
  const decimals = normalizeCurrencyDecimals(money.fraction_digits);
  return Number(money.amount_minor) / 10 ** decimals;
};

const createCanonicalPriceQuote = (product = {}) => {
  const summary = product.price_summary;
  if (!summary || typeof summary !== "object") return null;
  const moneyCurrent = getMoneyObjectAmount(summary.current_money);
  const summaryCurrent = Number(summary.current);
  const current =
    moneyCurrent !== null
      ? moneyCurrent
      : Number.isFinite(summaryCurrent)
      ? summaryCurrent
      : null;
  if (current === null) return null;
  const moneyOriginal = getMoneyObjectAmount(summary.original_money);
  const summaryOriginal = Number(summary.original);
  const original =
    moneyOriginal !== null
      ? moneyOriginal
      : Number.isFinite(summaryOriginal)
      ? summaryOriginal
      : current;
  const decimals = normalizeCurrencyDecimals(
    summary.current_money?.fraction_digits ?? summary.fraction_digits,
  );
  const roundedCurrent = roundMoney(current, decimals);
  const roundedOriginal = roundMoney(Math.max(original, current), decimals);
  const isDiscounted =
    summary.has_discount === true || roundedOriginal > roundedCurrent;
  return {
    current: roundedCurrent,
    original: isDiscounted ? roundedOriginal : roundedCurrent,
    base: roundMoney(
      toFiniteNumber(product.price, roundedCurrent) > 0
        ? toFiniteNumber(product.price, roundedCurrent)
        : roundedCurrent,
      decimals,
    ),
    preselectedOptions: 0,
    includesPreselectedOptions: summary.includes_preselected_options === true,
    isDiscounted,
    discountAmount: roundMoney(
      Math.max(roundedOriginal - roundedCurrent, 0),
      decimals,
    ),
    discountPercent:
      roundedOriginal > 0 && isDiscounted
        ? Math.round(
            ((roundedOriginal - roundedCurrent) / roundedOriginal) * 100,
          )
        : 0,
    currency:
      summary.current_money?.currency || summary.currency || product.currency,
    decimals,
    promotion: summary.promotion || null,
    source: "price_summary",
  };
};

export const createProductPriceQuote = ({
  product = {},
  options = product.options,
  discountedBasePrice,
  discountedOptionPrice,
  settings = {},
} = {}) => {
  const canonicalQuote = createCanonicalPriceQuote(product);
  if (canonicalQuote) return canonicalQuote;
  const decimals = normalizeCurrencyDecimals(settings.currency_decimals);
  const basePrice = Math.max(toFiniteNumber(product.price), 0);
  const strikedPrice = Math.max(toFiniteNumber(product.striked_price), basePrice);
  const optionTotals = getPreselectedOptionTotals(options);
  const currentOptions =
    discountedOptionPrice === undefined
      ? optionTotals.current
      : Math.max(toFiniteNumber(discountedOptionPrice), 0);
  const current = roundMoney(
    (discountedBasePrice === undefined
      ? basePrice
      : Math.max(toFiniteNumber(discountedBasePrice), 0)) + currentOptions,
    decimals,
  );
  const original = roundMoney(
    Math.max(strikedPrice + optionTotals.original, basePrice + optionTotals.current),
    decimals,
  );

  return {
    current,
    original,
    base: roundMoney(basePrice, decimals),
    preselectedOptions: roundMoney(currentOptions, decimals),
    includesPreselectedOptions: currentOptions > 0,
    isDiscounted: original > current,
    discountAmount: roundMoney(Math.max(original - current, 0), decimals),
    discountPercent:
      original > 0 && original > current
        ? Math.round(((original - current) / original) * 100)
        : 0,
    decimals,
    currency: product.currency,
    promotion: null,
    source: "local-fallback",
  };
};

export const formatPriceQuote = (
  quote,
  { settings = {}, language = "english" } = {},
) => {
  // Product/menu API values already include the selected storefront
  // multiplier. Always use that selected currency label on product surfaces;
  // otherwise a stale base Money label can mix KD and BD on the same PDP.
  // Checkout base Money uses its separate display-only renderer.
  const selectedCurrency =
    getCurrencyLabel(settings, language) || quote.currency;
  const moneySettings = {
    ...settings,
    currency_decimals: quote.decimals,
  };
  return {
    ...quote,
    currentText: formatMoney(quote.current, {
      settings: moneySettings,
      language,
      currency: selectedCurrency,
    }),
    originalText: formatMoney(quote.original, {
      settings: moneySettings,
      language,
      currency: selectedCurrency,
    }),
    discountText: formatMoney(quote.discountAmount, {
      settings: moneySettings,
      language,
      currency: selectedCurrency,
    }),
  };
};
